import { defineStore } from 'pinia';
import { useStore } from 'vuex';
import { computed, ref } from 'vue';
import { ApiSetupResponse } from '@/rennovation/planning-view/types/api/setup-response';
import { useI18n } from 'vue-i18n';
import { useAlertStore } from '@/rennovation/planning-view/store/useAlertStore';

export interface ExtraPayAccountDefinition {
	name: string;
	abbreviation: string;
	number: number;
}

const LOCALE_STORAGE_KEY = 'locale';

export const useSetupBridgeStore = defineStore('setupBridge', () => {
	const { t } = useI18n();
	const alertStore = useAlertStore();
	const vuex = useStore();

	const setup = computed(() => vuex.state.setup?.setup);
	const extraPayAccounts = computed(() => setup.value.extraPay_accounts);
	const activeLocale = ref<string>(localStorage.getItem(LOCALE_STORAGE_KEY) || 'en');
	const defaultLabels = computed(() => vuex.state.setup.defaultLabels);

	const loadSetup = async (): Promise<ApiSetupResponse> => {
		try {
			const result = await vuex.dispatch('setup/loadSetup');

			const targetConfig = vuex.state.setup?.setup;
			if (targetConfig && Array.isArray(targetConfig.extraPay_accounts)) {
				extraPayAccounts.value = targetConfig.extraPay_accounts;
			}

			return result;
		} catch (error) {
			console.error('SetupBridge: Error loading setup', error);
			const errorMessage = error?.response?.data?.message || t('data_load_failed');
			alertStore.showAlert(errorMessage, 'error');
			throw error;
		}
	};

	const loadLabelsFullInfo = async (labels) => {
		await vuex.dispatch('setup/loadDefaultLabels', labels);
	};

	const ensureLocale = async (): Promise<string> => {
		const cached = localStorage.getItem(LOCALE_STORAGE_KEY);
		if (cached) {
			activeLocale.value = cached;
			return cached;
		}

		if (setup.value?.locale_default) {
			localStorage.setItem('user_locale', setup.value.locale_default);
			activeLocale.value = setup.value.locale_default;
			return setup.value.locale_default;
		}

		try {
			const result = await vuex.dispatch('setup/loadSetup');
			const userLocale = result?.setup?.locale_default;

			if (userLocale) {
				localStorage.setItem(LOCALE_STORAGE_KEY, userLocale);
				activeLocale.value = userLocale;
				return userLocale;
			}
		} catch (error) {
			console.error('SetupBridge: Failed to load configuration waterfall', error);
		}

		return activeLocale.value;
	};

	return {
		setup,
		activeLocale,
		extraPayAccounts,
		defaultLabels,
		loadSetup,
		loadLabelsFullInfo,
		ensureLocale,
	};
});
